import { useState } from 'react';
import { Button, Input, Select } from 'semantic-ui-react';
import styled from '@emotion/styled';
import { formSelectService } from 'src/components/helpers';
import { GENDER_OPTIONS } from 'src/components/helpers/guests/genderOptions';
import useRememberCredentials from 'src/components/helpers/useRememberCredentials';
import RememberCredentialsCheckbox from 'src/components/helpers/RememberCredentialsCheckbox';
import Swal from 'sweetalert2';

const LoginContainer = styled.div`
  background: white;
  padding: 32px;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: 400px;
  margin: 40px auto;

  form {
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .login-title {
    text-align: center;
    color: #333;
    margin-bottom: 10px;
    font-size: 24px;
    font-weight: bold;
  }
  .login-subtitle {
    text-align: center;
    color: #666;
    margin-bottom: 24px;
    font-size: 16px;
    font-weight: normal;
  }
  .form-group {
    margin-bottom: 10px;
  }
  .styled-input {
    width: 100%;
    margin: 8px 0;
  }
  .styled-input.error input {
    border-color: #dc3545;
  }
  .error-message {
    color: red;
    font-size: 12px;
    margin: 0;
  }
  .styled-button {
    width: 100%;
    margin: 10px 0;
  }
  .error-heading {
    color: red;
    margin-bottom: 10px;
    text-align: center;
  }
`;

const RelationsLoginForm = (props) => {
  const { setStep, inputs, setInputs, checkRelationGuest } = props;
  const [errorInputs, setErrorInputs] = useState({});
  const [loading, setLoading] = useState(false);
  const { rememberCredentials, setRememberCredentials, persistCredentials } =
    useRememberCredentials({ setInputs });

  const verifyForm = () => {
    let mandatoryInputs = ['identification', 'phone', 'gender'];
    let errorInputsCheck = {};

    mandatoryInputs.forEach((input) => {
      if (!inputs[input]) {
        errorInputsCheck[input] = true;
      }
    });

    if (inputs['identification'] && inputs['identification'].length < 5) {
      errorInputsCheck['identification'] = true;
    }
    if (inputs['phone'] && inputs['phone'].length < 10) {
      errorInputsCheck['phone'] = true;
    }

    setErrorInputs(errorInputsCheck);
    return Object.values(errorInputsCheck).includes(true);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (verifyForm()) return;
    setLoading(true);

    persistCredentials({
      identification: inputs.identification,
      phone: inputs.phone,
    });

    try {
      const data = await checkRelationGuest({
        identification: inputs.identification,
        phone: inputs.phone,
      });
      if (!data) return;

      if (data.hasInscription) {
        Swal.fire({
          title: 'Ya cuentas con inscripción previa.',
          text: 'Ya tienes una inscripción activa para este semestre de Relaciones Sanas.',
          icon: 'info',
        });
        return;
      }

      if (data.hasCompletedClasses) {
        Swal.fire({
          title: 'Ya completaste Relaciones Sanas.',
          html: `
              <p>Si ya has recibido el seminario en el pasado y deseas volver a participar</p>
              <p><strong>Déjanos tus datos y llena el formulario.</strong></p>
              <p><strong>Daremos prioridad a las personas que aún no han realizado este seminario. </strong></p> <p>Si, una vez asignados los cupos, quedan espacios disponibles, nos pondremos en contacto contigo para confirmar tu participación.</p>
              <p>¡Gracias por tu interés!</p>
            `,
          icon: 'info',
          confirmButtonText: 'Continuar',
        }).then((result) => {
          if (result.value) {
            setStep?.('initial');
          }
        });
        return;
      }

      if (data.overLimit) {
        Swal.fire({
          title: 'Lo sentimos',
          text: 'Te informamos que se ha completado el cupo maximo de participantes para este semestre. Te invitamos a estar atento a la convocatoria del próximo semestre. Bendiciones.',
          icon: 'error',
        });
        return;
      }

      setStep?.('initial');
    } finally {
      setLoading(false);
    }
  };

  const handleInputs = (event) => {
    const { name, value } = event.target;
    setInputs({ ...inputs, [name]: value });
    setErrorInputs((prev) => ({ ...prev, [name]: false }));
  };

  const selectHandle = (a, { value, name }) => handleInputs({ target: { value, name } });

  return (
    <LoginContainer>
      <div className="login-title">Inscripción al Seminario de Relaciones Sanas</div>
      <div className="login-subtitle">
        Por favor, ingresa tu identificación y celular para iniciar el proceso de inscripción.
      </div>
      <form onSubmit={handleLogin}>
        {Object.values(errorInputs || {}).includes(true) && (
          <div className="error-heading">Por favor verifica los campos en rojo.</div>
        )}
        <div className="form-group">
          <div className={`styled-input${errorInputs['identification'] ? ' error' : ''}`}>
            <Input
              onChange={handleInputs}
              name="identification"
              value={inputs.identification}
              type="number"
              placeholder="Identificación"
              id="identification"
            />
          </div>
          {errorInputs['identification'] && (
            <div className="error-message">Identificación requerida (mínimo 5 dígitos).</div>
          )}
        </div>
        <div className="form-group">
          <div className={`styled-input${errorInputs['phone'] ? ' error' : ''}`}>
            <Input
              placeholder="Celular"
              id="phone"
              name="phone"
              type="number"
              value={inputs.phone}
              onChange={handleInputs}
            />
          </div>
          {errorInputs['phone'] && (
            <div className="error-message">Celular requerido (mínimo 10 dígitos).</div>
          )}
        </div>
        <RememberCredentialsCheckbox
          checked={rememberCredentials}
          onChange={setRememberCredentials}
        />
        {formSelectService({
          Select,
          handler: selectHandle,
          label: 'Genero',
          name: 'gender',
          value: inputs.gender || '',
          errorInputs,
          options: GENDER_OPTIONS,
        })}
        {errorInputs['gender'] && (
          <div className="error-message">Por favor selecciona tu género.</div>
        )}
        <div className="form-group">
          <Button className="styled-button" primary disabled={loading} type="submit">
            {loading ? 'Entrando...' : 'Comenzar'}
          </Button>
        </div>
      </form>
    </LoginContainer>
  );
};

export default RelationsLoginForm;
